'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

type ConnectionState = 'connected' | 'reconnecting' | 'disconnected';
type EventHandler = (data: any) => void;

interface FileData {
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
}

interface WebSocketContextValue {
  isConnected: boolean;
  connectionState: ConnectionState;
  onlineUsers: string[];
  sendMessage: (chatId: string, content: string, messageType?: string, fileData?: FileData, replyTo?: string) => void;
  editMessage: (messageId: string, chatId: string, newContent: string) => void;
  deleteMessage: (messageId: string, chatId: string) => void;
  sendReadReceipt: (chatId: string, messageIds: string[]) => void;
  sendTyping: (chatId: string) => void;
  sendReaction: (messageId: string, chatId: string, emoji: string) => void;
  sendCallOffer: (targetId: string, chatId: string, callType: 'audio' | 'video', offer: RTCSessionDescriptionInit) => void;
  sendCallAnswer: (targetId: string, chatId: string, answer: RTCSessionDescriptionInit) => void;
  sendIceCandidate: (targetId: string, chatId: string, candidate: RTCIceCandidate) => void;
  sendCallEnd: (targetId: string, chatId: string) => void;
  sendCallReject: (targetId: string, chatId: string) => void;
  sendCallLog: (chatId: string, callType: 'audio' | 'video', status: 'completed' | 'missed' | 'rejected' | 'no_answer', duration?: number) => void;
  subscribe: (event: string, handler: EventHandler) => () => void;
}

export type UseWebSocketReturn = WebSocketContextValue;

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);

  const socketRef = useRef<WebSocket | null>(null);
  const subscribersRef = useRef<Map<string, Set<EventHandler>>>(new Map());
  const reconnectAttemptRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pongTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMessageTimestampRef = useRef<string | null>(null);
  const typingThrottleRef = useRef<Map<string, number>>(new Map());
  const isMountedRef = useRef<boolean>(true);
  const isConnectingRef = useRef<boolean>(false);

  const getBackoffDelay = useCallback((attempt: number): number => {
    return Math.min(Math.pow(2, attempt) * 1000, 30000);
  }, []);

  const dispatch = useCallback((event: string, data: any) => {
    const handlers = subscribersRef.current.get(event);
    if (handlers) {
      handlers.forEach((handler) => { try { handler(data); } catch (err) { console.error(`[WS] handler error for "${event}":`, err); } });
    }
  }, []);

  const send = useCallback((data: object) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(data));
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatIntervalRef.current = setInterval(() => {
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        send({ type: 'ping' });
        pongTimeoutRef.current = setTimeout(() => { socketRef.current?.close(); }, 5000);
      }
    }, 25000);
  }, [send]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) { clearInterval(heartbeatIntervalRef.current); heartbeatIntervalRef.current = null; }
    if (pongTimeoutRef.current) { clearTimeout(pongTimeoutRef.current); pongTimeoutRef.current = null; }
  }, []);

  const fetchToken = useCallback(async (): Promise<string | null> => {
    try {
      const res = await fetch('/api/auth/ws-token', { credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      return data.token || null;
    } catch { return null; }
  }, []);

  const connect = useCallback(async () => {
    if (!isMountedRef.current || isConnectingRef.current) return;
    isConnectingRef.current = true;

    const token = await fetchToken();
    if (!token) { isConnectingRef.current = false; setConnectionState('disconnected'); return; }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;

      ws.onopen = () => {
        if (!isMountedRef.current) { ws.close(); return; }
        isConnectingRef.current = false;
        setConnectionState('connected');
        reconnectAttemptRef.current = 0;
        startHeartbeat();
        if (lastMessageTimestampRef.current) { send({ type: 'request_missed', since: lastMessageTimestampRef.current }); }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') { if (pongTimeoutRef.current) { clearTimeout(pongTimeoutRef.current); pongTimeoutRef.current = null; } return; }
          if (data.type === 'init') { setOnlineUsers(data.onlineUsers || []); if (data.timestamp) lastMessageTimestampRef.current = data.timestamp; dispatch('init', data); return; }
          if (data.type === 'user_online') { setOnlineUsers(prev => prev.includes(data.employeeId) ? prev : [...prev, data.employeeId]); dispatch('user_online', data); return; }
          if (data.type === 'user_offline') { setOnlineUsers(prev => prev.filter(id => id !== data.employeeId)); dispatch('user_offline', data); return; }
          if (data.type === 'new_message' && data.message?.createdAt) { lastMessageTimestampRef.current = data.message.createdAt; }
          dispatch(data.type, data);
        } catch (err) { console.error('[WS] parse error:', err); }
      };

      ws.onclose = (event) => {
        isConnectingRef.current = false;
        stopHeartbeat();
        socketRef.current = null;
        if (!isMountedRef.current) return;
        if (event.code === 4001) { setConnectionState('disconnected'); return; }
        setConnectionState('reconnecting');
        const delay = getBackoffDelay(reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => { if (isMountedRef.current) connect(); }, delay);
      };

      ws.onerror = () => { console.warn('[WS] connection error — will retry'); };
    } catch (err) {
      isConnectingRef.current = false;
      if (isMountedRef.current) {
        setConnectionState('reconnecting');
        const delay = getBackoffDelay(reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => { if (isMountedRef.current) connect(); }, delay);
      }
    }
  }, [fetchToken, startHeartbeat, stopHeartbeat, send, dispatch, getBackoffDelay]);

  useEffect(() => {
    isMountedRef.current = true;
    if (user) { connect(); }
    return () => {
      isMountedRef.current = false;
      stopHeartbeat();
      if (reconnectTimeoutRef.current) { clearTimeout(reconnectTimeoutRef.current); reconnectTimeoutRef.current = null; }
      if (socketRef.current) { socketRef.current.close(1000, 'unmount'); socketRef.current = null; }
    };
  }, [user]);

  const sendMessage = useCallback((chatId: string, content: string, messageType?: string, fileData?: FileData, replyTo?: string) => {
    send({ type: 'send_message', chatId, content, messageType: messageType || 'text', ...(fileData && { fileData }), ...(replyTo && { replyTo }) });
  }, [send]);

  const editMessage = useCallback((messageId: string, chatId: string, newContent: string) => {
    send({ type: 'edit_message', messageId, chatId, content: newContent });
  }, [send]);

  const deleteMessage = useCallback((messageId: string, chatId: string) => {
    send({ type: 'delete_message', messageId, chatId });
  }, [send]);

  const sendReadReceipt = useCallback((chatId: string, messageIds: string[]) => {
    send({ type: 'read_receipt', chatId, messageIds });
  }, [send]);

  const sendTyping = useCallback((chatId: string) => {
    const now = Date.now();
    const last = typingThrottleRef.current.get(chatId) || 0;
    if (now - last < 2000) return;
    typingThrottleRef.current.set(chatId, now);
    send({ type: 'typing', chatId });
  }, [send]);

  const sendReaction = useCallback((messageId: string, chatId: string, emoji: string) => {
    send({ type: 'reaction', messageId, chatId, emoji });
  }, [send]);

  const sendCallOffer = useCallback((targetId: string, chatId: string, callType: 'audio' | 'video', offer: RTCSessionDescriptionInit) => {
    send({ type: 'call_offer', targetId, chatId, callType, offer });
  }, [send]);

  const sendCallAnswer = useCallback((targetId: string, chatId: string, answer: RTCSessionDescriptionInit) => {
    send({ type: 'call_answer', targetId, chatId, answer });
  }, [send]);

  const sendIceCandidate = useCallback((targetId: string, chatId: string, candidate: RTCIceCandidate) => {
    send({ type: 'ice_candidate', targetId, chatId, candidate });
  }, [send]);

  const sendCallEnd = useCallback((targetId: string, chatId: string) => {
    send({ type: 'call_end', targetId, chatId });
  }, [send]);

  const sendCallReject = useCallback((targetId: string, chatId: string) => {
    send({ type: 'call_reject', targetId, chatId });
  }, [send]);

  const sendCallLog = useCallback((chatId: string, callType: 'audio' | 'video', status: 'completed' | 'missed' | 'rejected' | 'no_answer', duration?: number) => {
    send({ type: 'call_log', chatId, callType, status, duration });
  }, [send]);

  const subscribe = useCallback((event: string, handler: EventHandler): (() => void) => {
    if (!subscribersRef.current.has(event)) subscribersRef.current.set(event, new Set());
    subscribersRef.current.get(event)!.add(handler);
    return () => {
      const handlers = subscribersRef.current.get(event);
      if (handlers) { handlers.delete(handler); if (handlers.size === 0) subscribersRef.current.delete(event); }
    };
  }, []);

  const value: WebSocketContextValue = {
    isConnected: connectionState === 'connected',
    connectionState,
    onlineUsers,
    sendMessage,
    editMessage,
    deleteMessage,
    sendReadReceipt,
    sendTyping,
    sendReaction,
    sendCallOffer,
    sendCallAnswer,
    sendIceCandidate,
    sendCallEnd,
    sendCallReject,
    sendCallLog,
    subscribe,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

export function useWebSocket(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    // Return no-op values if used outside provider (e.g. on public pages)
    return {
      isConnected: false,
      connectionState: 'disconnected',
      onlineUsers: [],
      sendMessage: () => {},
      editMessage: () => {},
      deleteMessage: () => {},
      sendReadReceipt: () => {},
      sendTyping: () => {},
      sendReaction: () => {},
      sendCallOffer: () => {},
      sendCallAnswer: () => {},
      sendIceCandidate: () => {},
      sendCallEnd: () => {},
      sendCallReject: () => {},
      sendCallLog: () => {},
      subscribe: () => () => {},
    };
  }
  return ctx;
}
